/** @jsxRuntime classic */
/** @jsx jsx */

import { useCallback, useEffect, useState } from 'react';
import { css, jsx, keyframes } from '@emotion/react';
import { Colors } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { sharedHeadingStyles } from './SplitColorHero';

export interface AnimatedHeadingProps {
  staticText: string;
  rotatingText: { text: string }[];
}

const Styles = labelStyles('AnimatedHeading', {
  root: css({
    width: 'max-content',
    minWidth: '100%',
    height: '80px',
    position: 'relative',
    overflow: 'hidden',

    [MEDIA_MIN_WIDTH.medium]: {
      height: '96px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      height: '128px',
    },
  }),

  staticText: css({
    position: 'relative',
    zIndex: 1,
    backgroundColor: Colors.neutral100,
  }),

  rotatingText: css({
    position: 'absolute',
    top: '100%',
    left: 0,
    whiteSpace: 'nowrap',
    color: Colors.zapier,
    animationDuration: '500ms',
    animationTimingFunction: 'cubic-bezier(0.65, 0, 0.35, 1)',
    animationFillMode: 'forwards',

    '&.animate-in': {
      animationName: keyframes({
        from: { transform: 'translateY(0)' },
        to: { transform: 'translateY(-100%)' },
      }),
    },

    '&.animate-out': {
      animationName: keyframes({
        from: { transform: 'translateY(-100%)' },
        to: { transform: 'translateY(-200%)' },
      }),
    },
  }),
});

export function AnimatedHeading(props: AnimatedHeadingProps) {
  const [rotatingTextIndex, setRotatingTextIndex] = useState({
    current: 0,
    previous: -1,
  });

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const interval = setInterval(() => {
      setRotatingTextIndex(({ current }) => {
        return {
          current: current < props.rotatingText.length - 1 ? current + 1 : 0,
          previous: current,
        };
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [props.rotatingText]);

  /*
    Returns the heading text with proper punctuation for more natural readout
    by screen readers
  */
  const getAriaLabel = useCallback(() => {
    const list = props.rotatingText
      .map(({ text }, index) => {
        return index < props.rotatingText.length - 1 ? text : `and ${text}`;
      })
      .join(', ');
    return `${props.staticText}: ${list}.`;
  }, [props.staticText, props.rotatingText]);

  const getClassName = (index: number) => {
    if (index === rotatingTextIndex.current) return 'animate-in';
    if (index === rotatingTextIndex.previous) return 'animate-out';
    return '';
  };

  return (
    <div>
      <h1
        aria-label={getAriaLabel()}
        css={[sharedHeadingStyles, Styles.root]}
        role="marquee"
      >
        <span css={Styles.staticText}>{props.staticText}</span>
        {props.rotatingText.map(({ text }, index) => (
          <span
            className={getClassName(index)}
            css={Styles.rotatingText}
            key={`${text}-${index}`}
          >
            {text}
          </span>
        ))}
      </h1>
    </div>
  );
}
